/**
 * Condition pages (/conditions/:slug).
 *
 * Copy is drafted for practitioner sign-off. Pages render with noindex and
 * stay out of the sitemap until `reviewed` is set to true. Keep language
 * supportive ("may help"), never diagnostic or curative.
 */

export const conditionsHubReviewed = false

export const conditions = [
  {
    slug: 'neck-back-pain',
    name: 'Neck & Back Pain',
    title: 'Neck & Back Pain Treatment in Midtown Toronto',
    description:
      'Massage and athletic therapy for neck and back pain at Yonge & Eglinton. Hands-on care, movement assessment, and a plan to keep you moving.',
    h1: 'Neck & Back Pain Treatment at Yonge & Eglinton',
    intro:
      'Neck and back pain is the most common reason people visit us. We combine hands-on treatment with movement assessment and exercise to help you feel better now and stay that way.',
    image: '/services/back-massage.jpg',
    imageAlt: 'Massage therapist treating a client’s upper back',
    sections: [
      {
        heading: 'Why neck and back pain happens',
        body: [
          'Most neck and back pain is not caused by a single event. Long hours sitting, stress, poor sleep, a change in training, or lifting something awkwardly can all add up until your muscles and joints become sensitive and stiff.',
          'That is why we look at the whole picture — how you sit, move, work, and train — rather than only the spot that hurts.',
        ],
      },
      {
        heading: 'How we can help',
        body: [
          'Massage therapy helps reduce muscle tension, improve mobility, and relieve everyday aches and stiffness. Athletic therapy assesses the movement patterns that may be contributing, and combines hands-on care with targeted exercise to restore function through the neck and back.',
          'Acupuncture may also be included to help calm sensitive tissues and support pain relief.',
        ],
      },
    ],
    symptomsTitle: 'You might notice',
    symptoms: [
      'Stiffness when you first get up or after sitting',
      'Aching between the shoulder blades',
      'Low back pain when bending or lifting',
      'Tension headaches that start in the neck',
      'Reduced range of motion turning your head',
    ],
    whenToSeeDoctor:
      'See a physician promptly if back pain follows a fall or accident, or comes with numbness in the groin area, loss of bladder or bowel control, unexplained weight loss, fever, or weakness in the legs.',
    services: ['massage-therapy', 'athletic-therapy', 'acupuncture'],
    practitioners: ['olivia-embry', 'brandon-schneider', 'jazmin-ulalan', 'miranda-manolakos'],
    faqs: [
      {
        q: 'Should I see a massage therapist or an athletic therapist for back pain?',
        a: 'Either is a good place to start. Choose massage therapy if tension and stiffness are the main issue, or athletic therapy if pain started with an injury or you want a structured exercise plan. Your practitioner will refer you across the team if needed.',
      },
      {
        q: 'How many sessions will I need?',
        a: 'It depends on how long you have had symptoms and what is contributing. Many people notice a change within the first few visits. Your practitioner will give you an estimate after your assessment.',
      },
    ],
    related: ['desk-worker-pain', 'sciatica', 'shoulder-pain'],
    reviewed: false,
  },
  {
    slug: 'tmj-headaches',
    name: 'TMJ & Headaches',
    title: 'TMJ, Jaw Pain & Headache Relief in Toronto',
    description:
      'Relief for jaw pain, clenching, and tension headaches in Midtown Toronto with TMJ-trained RMTs, intra-oral massage, and acupuncture.',
    h1: 'TMJ, Jaw Pain & Tension Headaches',
    intro:
      'Jaw tension, clenching, and headaches often travel together. Our TMJ-trained massage therapists treat the jaw, face, neck, and shoulders as one connected system.',
    image: '/services/pain-management.jpg',
    imageAlt: 'Therapist treating the jaw and neck of a client',
    sections: [
      {
        heading: 'The jaw–neck–headache connection',
        body: [
          'The muscles that open and close your jaw work closely with the muscles of your head and neck. Clenching during the day, grinding at night, stress, and long hours at a screen can all overload them, which often shows up as jaw pain, clicking, facial tension, or headaches.',
        ],
      },
      {
        heading: 'How we can help',
        body: [
          'Massage therapy relieves tension through the neck, jaw, and surrounding muscles that may contribute to headaches and discomfort. When appropriate and with your consent, treatment includes intra-oral massage to reach the jaw muscles directly.',
          'Acupuncture can support pain relief, nervous system regulation, and tension reduction. Your therapist will also share self-care strategies for between visits.',
        ],
      },
    ],
    symptomsTitle: 'Signs of TMJ-related tension',
    symptoms: [
      'Jaw pain, clicking, or locking',
      'Clenching or grinding, especially at night',
      'Headaches around the temples',
      'Facial or ear-area tension',
      'Neck and shoulder tightness',
    ],
    whenToSeeDoctor:
      'See a physician or dentist if your jaw locks open or closed, you have sudden severe headaches, or headaches come with vision changes, weakness, or numbness.',
    services: ['tmj-treatment', 'massage-therapy', 'acupuncture'],
    practitioners: ['olivia-embry'],
    faqs: [
      {
        q: 'Can massage therapy help with TMJ?',
        a: 'Massage therapy can help reduce tension in the jaw and surrounding muscles, which may ease pain and related headaches. It works well alongside care from your dentist.',
      },
      {
        q: 'Do I need a referral from my dentist?',
        a: 'No referral is needed. If your dentist has given you a night guard or other guidance, let your therapist know so treatment can complement it.',
      },
    ],
    related: ['neck-back-pain', 'desk-worker-pain'],
    reviewed: false,
  },
  {
    slug: 'running-injuries',
    name: 'Running & Sports Injuries',
    title: 'Running & Sports Injury Rehab in Toronto',
    description:
      'Athletic therapy and sports massage for running and sports injuries at Yonge & Eglinton. Assessment, rehab, and a clear return-to-run plan.',
    h1: 'Running & Sports Injury Rehab in Midtown Toronto',
    intro:
      'From sore knees and tight hips to sprains and strains, we help runners and recreational athletes recover, rebuild, and get back to training with confidence.',
    image: '/services/sports-massage.jpg',
    imageAlt: 'Therapist treating a runner’s lower leg',
    sections: [
      {
        heading: 'Common running and sports injuries we see',
        body: [
          'Overuse injuries are common when training volume, pace, or terrain changes quickly. We regularly work with knee, hip, shin, ankle, and foot pain, along with muscle strains and joint sprains from sport.',
        ],
      },
      {
        heading: 'How we can help',
        body: [
          'Athletic therapy assesses how you move and load, then combines hands-on techniques with therapeutic exercise to restore strength and mobility. You get a staged plan that takes you from managing pain back to full training.',
          'Sports massage supports recovery between sessions and helps manage tightness before it becomes an injury.',
        ],
      },
    ],
    symptomsTitle: 'Common concerns',
    symptoms: [
      'Knee pain on stairs or downhill running',
      'Shin pain that builds with mileage',
      'Hip and IT band tightness',
      'Ankle sprains and heel or foot pain',
      'Hamstring and calf strains',
    ],
    whenToSeeDoctor:
      'See a physician if you cannot bear weight, have significant swelling or deformity after an injury, or have pain that worsens at rest or wakes you at night.',
    services: ['athletic-therapy', 'sports-massage', 'massage-therapy'],
    practitioners: ['miranda-manolakos', 'jazmin-ulalan', 'brandon-schneider'],
    faqs: [
      {
        q: 'Do I have to stop running?',
        a: 'Not always. Often we adjust your training load rather than stopping completely. Your therapist will help you find what you can keep doing while you recover.',
      },
      {
        q: 'How soon should I book after an injury?',
        a: 'Earlier is usually better. An assessment in the first days or weeks helps you manage the injury well and avoid compensations that slow recovery.',
      },
    ],
    related: ['shoulder-pain', 'sciatica', 'neck-back-pain'],
    reviewed: false,
  },
  {
    slug: 'desk-worker-pain',
    name: 'Desk Worker Pain',
    title: 'Desk Posture, Neck & Shoulder Pain Relief',
    description:
      'Relief for desk-related neck, shoulder, and back pain in Midtown Toronto. Massage and athletic therapy plus simple habits for the workday.',
    h1: 'Neck, Shoulder & Back Pain for Desk Workers',
    intro:
      'Long days at a desk can leave your neck, shoulders, and back stiff and sore. We help relieve the tension and build the strength and habits that make workdays easier.',
    image: '/services/seated-massage.jpg',
    imageAlt: 'Therapist treating a client’s neck and shoulders in a seated position',
    sections: [
      {
        heading: 'Why desk work causes pain',
        body: [
          'Sitting in one position for hours, screens below eye level, and stress all load the same muscles day after day. Over time that often leads to neck stiffness, tension between the shoulder blades, low back aches, and headaches.',
        ],
      },
      {
        heading: 'How we can help',
        body: [
          'Massage therapy relieves persistent tightness and helps your body move with more ease day to day. Athletic therapy combines manual therapy with mobility and strengthening work to address postural discomfort and keep it from coming back.',
          'We also share practical changes to your workstation and short movement breaks that fit into a busy day.',
        ],
      },
    ],
    symptomsTitle: 'You might notice',
    symptoms: [
      'Neck stiffness by the end of the day',
      'Tension between the shoulder blades',
      'Low back aches after sitting',
      'Headaches that build through the afternoon',
      'Tingling or tightness into the forearms and wrists',
    ],
    whenToSeeDoctor:
      'See a physician if you have persistent numbness or weakness in your arms or hands, or pain that does not change with movement or rest.',
    services: ['massage-therapy', 'athletic-therapy', 'acupuncture'],
    practitioners: ['olivia-embry', 'jazmin-ulalan', 'miranda-manolakos'],
    faqs: [
      {
        q: 'Is a standing desk the answer?',
        a: 'Changing position often matters more than any single posture. A standing desk can help if you use it to vary your position through the day rather than standing still for hours.',
      },
      {
        q: 'Can I book before or after work?',
        a: 'Yes. We are open until 8:30 pm Monday to Friday and on Saturday mornings.',
      },
    ],
    related: ['neck-back-pain', 'tmj-headaches', 'shoulder-pain'],
    reviewed: false,
  },
  {
    slug: 'shoulder-pain',
    name: 'Shoulder Pain',
    title: 'Shoulder Pain Treatment in Midtown Toronto',
    description:
      'Athletic and massage therapy for shoulder pain, stiffness, and rotator cuff issues at Yonge & Eglinton. Restore strength and range of motion.',
    h1: 'Shoulder Pain Treatment at Yonge & Eglinton',
    intro:
      'Whether your shoulder hurts reaching overhead, lifting, or sleeping, we assess what is driving it and build a plan to restore comfortable, strong movement.',
    image: '/services/cupping.jpg',
    imageAlt: 'Therapist treating a client’s shoulder with cupping',
    sections: [
      {
        heading: 'Common causes of shoulder pain',
        body: [
          'The shoulder is the most mobile joint in the body, which makes it sensitive to changes in load. Rotator cuff irritation, overuse from sport or the gym, stiffness after an injury, and posture-related tension are common reasons people come to see us.',
        ],
      },
      {
        heading: 'How we can help',
        body: [
          'Athletic therapy assesses shoulder strength and movement and guides you through progressive exercise to rebuild capacity. Massage therapy, cupping, and acupuncture can help ease tension and improve mobility through the shoulder, upper back, and neck.',
          'If you are preparing for or recovering from shoulder surgery, we can help you build strength beforehand and restore movement and function afterwards.',
        ],
      },
    ],
    symptomsTitle: 'You might notice',
    symptoms: [
      'Pain reaching overhead or behind your back',
      'Pain lying on the affected side',
      'Weakness lifting or carrying',
      'Stiffness and reduced range of motion',
      'Clicking or catching with movement',
    ],
    whenToSeeDoctor:
      'See a physician if you cannot lift your arm after a fall, the shoulder looks deformed, or shoulder pain comes with chest pain or shortness of breath.',
    services: ['athletic-therapy', 'massage-therapy', 'acupuncture'],
    practitioners: ['miranda-manolakos', 'brandon-schneider'],
    faqs: [
      {
        q: 'Should I rest my shoulder?',
        a: 'Short-term rest from painful activities can help, but complete rest often leads to stiffness. Your therapist will help you find movement that is safe while it recovers.',
      },
      {
        q: 'Can you help after shoulder surgery?',
        a: 'Yes. Athletic therapy supports pre- and post-surgical clients, working alongside the guidance from your surgeon.',
      },
    ],
    related: ['desk-worker-pain', 'neck-back-pain', 'running-injuries'],
    reviewed: false,
  },
  {
    slug: 'sciatica',
    name: 'Sciatica & Hip Pain',
    title: 'Sciatica & Hip Pain Treatment in Toronto',
    description:
      'Care for sciatica, low back, and hip pain in Midtown Toronto. Massage therapy, athletic therapy, and acupuncture to ease pain and restore movement.',
    h1: 'Sciatica & Hip Pain Treatment in Midtown Toronto',
    intro:
      'Pain that travels from your low back or buttock into your leg can be unsettling. We help ease symptoms and guide you back to comfortable movement.',
    image: '/services/massage-therapy.jpg',
    imageAlt: 'Therapist treating a client’s low back and hip',
    sections: [
      {
        heading: 'What is sciatica?',
        body: [
          '“Sciatica” describes pain that follows the path of the sciatic nerve, from the low back or buttock into the back of the leg. It often comes from irritation around the low back or hip, and it commonly improves with the right mix of movement and care.',
        ],
      },
      {
        heading: 'How we can help',
        body: [
          'Massage therapy can reduce tension through the low back, hips, and glutes. Athletic therapy guides you through movement and strengthening that calm symptoms and build tolerance for daily activities. Acupuncture may also be used to support pain relief.',
        ],
      },
    ],
    symptomsTitle: 'You might notice',
    symptoms: [
      'Pain from the buttock into the back of the thigh or leg',
      'Tingling or pins and needles in the leg',
      'Pain that is worse sitting for long periods',
      'Tight hips and glutes',
      'Low back stiffness',
    ],
    whenToSeeDoctor:
      'See a physician urgently if leg pain comes with numbness in the groin area, loss of bladder or bowel control, or weakness that is getting worse.',
    services: ['massage-therapy', 'athletic-therapy', 'acupuncture'],
    practitioners: ['brandon-schneider', 'jazmin-ulalan', 'miranda-manolakos'],
    faqs: [
      {
        q: 'Is it safe to exercise with sciatica?',
        a: 'Gentle, guided movement is usually helpful. Your therapist will help you choose exercises that ease symptoms rather than aggravate them.',
      },
      {
        q: 'How long does sciatica take to improve?',
        a: 'Many cases improve over several weeks. Your practitioner will review your progress and refer you to a physician if symptoms are not changing as expected.',
      },
    ],
    related: ['neck-back-pain', 'running-injuries', 'desk-worker-pain'],
    reviewed: false,
  },
  {
    slug: 'pregnancy-postpartum',
    name: 'Pregnancy & Postpartum',
    title: 'Pregnancy & Postpartum Care in Midtown Toronto',
    description:
      'Prenatal and postpartum massage and naturopathic support at Yonge & Eglinton for back pain, tension, sleep, energy, and recovery.',
    h1: 'Pregnancy & Postpartum Care at Yonge & Eglinton',
    intro:
      'Your body changes quickly through pregnancy and after birth. We offer hands-on and whole-body care to help you feel more comfortable at every stage.',
    image: '/services/prenatal.jpg',
    imageAlt: 'Pregnant client receiving side-lying massage therapy',
    sections: [
      {
        heading: 'Common concerns in pregnancy and postpartum',
        body: [
          'As your body adapts, it is common to feel low back and hip pain, neck and shoulder tension, heaviness in the legs, and disrupted sleep. After birth, feeding, carrying, and lifting place new demands on the upper back, arms, and core.',
        ],
      },
      {
        heading: 'How we can help',
        body: [
          'Massage therapy supports changing muscles and joints while helping reduce tension and discomfort throughout pregnancy and postpartum recovery. Treatment is done side-lying or in supported positions for comfort.',
          'Naturopathic medicine can support energy, recovery, hormones, and overall wellness during pregnancy and postpartum.',
        ],
      },
    ],
    symptomsTitle: 'We often help with',
    symptoms: [
      'Low back, hip, and pelvic discomfort',
      'Neck and shoulder tension',
      'Swelling and heaviness in the legs',
      'Sleep and stress',
      'Postpartum upper back and wrist strain',
    ],
    whenToSeeDoctor:
      'Contact your doctor or midwife right away for bleeding, severe abdominal pain, sudden swelling of the face or hands, severe headaches, or reduced baby movement.',
    services: ['prenatal-massage', 'massage-therapy', 'naturopathic-medicine'],
    practitioners: ['jazmin-ulalan', 'alana-shnier'],
    faqs: [
      {
        q: 'Is massage safe during pregnancy?',
        a: 'Massage therapy can be adapted for each stage of pregnancy. Let your therapist know how far along you are and share any guidance from your doctor or midwife.',
      },
      {
        q: 'When can I come in after giving birth?',
        a: 'Many people book once they feel ready and have been cleared by their healthcare provider. Your therapist will adapt treatment to your recovery.',
      },
    ],
    related: ['neck-back-pain', 'sciatica'],
    reviewed: false,
  },
]

export const conditionBySlug = (slug) => conditions.find((c) => c.slug === slug)
